import { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminLike } from "@/lib/roles";
import { fetchBlobUrl } from "@/lib/api";
import api from "@/lib/api";
import {
  Megaphone, Trophy, Heart, ArrowLeft, Pencil, Plus, Star, Pin, Trash2, RotateCcw,
  EyeOff, Eye, Calendar, User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate as fmtDateSafe } from "@/lib/dates";

const SECTION_META = {
  mural:       { icon: Megaphone, overline: "Mural del Club" },
  logros:      { icon: Trophy,    overline: "Logros & Hitos" },
  tradiciones: { icon: Heart,     overline: "Tradiciones Ferro" },
};

const BRANCH_LABEL = { all: "Todo el Club", tackle: "Tackle", flag_masculino: "Flag Masculino", flag_femenino: "Flag Femenino" };
const STATUS_LABEL = { draft: "Borrador", published: "Publicado", unpublished: "Despublicado", trashed: "Papelera" };
const STATUS_COLOR = { draft: "#71717A", published: "#34C759", unpublished: "#FF9F0A", trashed: "#FF3B30" };

const fmtDate = (iso) => fmtDateSafe(iso, { year: "numeric", month: "short", day: "numeric" }, "");

export default function FerroSection() {
  const { sectionId } = useParams();
  const nav = useNavigate();
  const meta = SECTION_META[sectionId];
  const { user } = useAuth();
  const isAdmin = isAdminLike(user?.role);

  const [section, setSection] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrash, setShowTrash] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [editingSectionMeta, setEditingSectionMeta] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get(`/ferro/sections/${sectionId}`),
        api.get(`/ferro/sections/${sectionId}/posts${showTrash ? "/trash" : ""}`),
      ]);
      setSection(s.data);
      setPosts(p.data);
    } catch {
      toast.error("No se pudo cargar la sección");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (meta) load(); }, [sectionId, showTrash]);

  const publicPosts = useMemo(() => posts.filter(p => p.status === "published"), [posts]);

  if (!meta) return <Navigate to="/somos-ferro" replace />;
  const Icon = meta.icon;

  const visible = adminMode ? posts : publicPosts;

  const doAction = async (fn, ok = "Listo") => {
    try { await fn(); toast.success(ok); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1200px]" data-testid={`ferro-section-${sectionId}`}>
        <Link to="/somos-ferro" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#A1A1AA] hover:text-cyan-400 mb-6" data-testid="ferro-back">
          <ArrowLeft size={14}/> Volver a Somos Ferro
        </Link>

        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="overline text-cyan-400 flex items-center gap-2">
              <Icon size={14}/> {meta.overline}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-none mt-2">
              {section?.title || meta.overline}
            </h1>
            <p className="text-[#A1A1AA] mt-2 max-w-2xl text-sm">{section?.description || ""}</p>
          </div>
          {isAdmin && (
            <div className="flex gap-2 flex-wrap" data-testid="ferro-admin-toolbar">
              <button onClick={() => setAdminMode(v => !v)} className={`btn-ghost text-xs flex items-center gap-1.5 ${adminMode ? "border-cyan-400 text-cyan-400" : ""}`} data-testid="ferro-toggle-admin">
                {adminMode ? <Eye size={12}/> : <EyeOff size={12}/>} {adminMode ? "Modo público" : "Administrar"}
              </button>
              {adminMode && (
                <>
                  <button onClick={() => setEditingSectionMeta(true)} className="btn-ghost text-xs flex items-center gap-1.5" data-testid="ferro-edit-section">
                    <Pencil size={12}/> Editar sección
                  </button>
                  <button onClick={() => nav(`/somos-ferro/${sectionId}/new`)} className="btn-primary text-xs flex items-center gap-1.5" data-testid="ferro-new-post">
                    <Plus size={12}/> Nueva publicación
                  </button>
                  <button onClick={() => setShowTrash(v => !v)} className={`btn-ghost text-xs flex items-center gap-1.5 ${showTrash ? "border-[#FF3B30] text-[#FF3B30]" : ""}`} data-testid="ferro-toggle-trash">
                    <Trash2 size={12}/> {showTrash ? "Volver a activas" : "Papelera"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {loading && <div className="tactical-card p-10 text-center text-[#71717A]">Cargando…</div>}
        {!loading && visible.length === 0 && (
          <div className="tactical-card p-10 text-center text-[#71717A]">
            {adminMode ? (showTrash ? "Papelera vacía." : "Aún no hay publicaciones. Crea la primera con \"Nueva publicación\".") : "Aún no hay publicaciones en esta sección."}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visible.map(p => (
            <PostCard key={p.id} p={p} sectionId={sectionId} isAdmin={isAdmin && adminMode} onAction={doAction} inTrash={showTrash}/>
          ))}
        </div>

        {editingSectionMeta && (
          <SectionMetaEditor section={section} onClose={() => setEditingSectionMeta(false)} onSaved={(s) => { setSection(s); setEditingSectionMeta(false); toast.success("Sección actualizada"); }}/>
        )}

        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

function PostCard({ p, sectionId, isAdmin, onAction, inTrash }) {
  const [cover, setCover] = useState(null);
  useEffect(() => {
    const coverImg = p.images?.find(i => i.id === p.cover_image_id) || p.images?.[0];
    if (!coverImg) { setCover(null); return; }
    let alive = true;
    fetchBlobUrl(`/ferro/images/${coverImg.storage_path}`).then(u => { if (alive) setCover(u); });
    return () => { alive = false; };
  }, [p]);

  return (
    <article className="tactical-card overflow-hidden flex flex-col" data-testid={`post-card-${p.id}`} style={p.is_featured ? { borderColor: "#FACC15" } : undefined}>
      {cover && (
        <div className="w-full aspect-[16/9] bg-[#0A0A0A] border-b border-[#27272A]">
          <img src={cover} alt="" className="w-full h-full object-cover"/>
        </div>
      )}
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            {p.is_pinned && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-cyan-400 text-cyan-400 flex items-center gap-1"><Pin size={9}/>Fijado</span>}
            {p.is_featured && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FACC15] text-[#FACC15] flex items-center gap-1"><Star size={9}/>Destacado</span>}
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#27272A] text-[#A1A1AA]">{BRANCH_LABEL[p.branch] || p.branch}</span>
            {isAdmin && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: STATUS_COLOR[p.status], color: STATUS_COLOR[p.status] }}>{STATUS_LABEL[p.status]}</span>
            )}
          </div>
        </div>
        <h3 className="font-display text-xl text-white leading-tight">{p.title}</h3>
        {p.summary && <p className="text-sm text-[#A1A1AA] line-clamp-3">{p.summary}</p>}
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#71717A] mt-1">
          <span className="flex items-center gap-1"><Calendar size={10}/>{fmtDate(p.event_date || p.published_at || p.created_at)}</span>
          {p.author_name && <span className="flex items-center gap-1"><UserIcon size={10}/>{p.author_name}</span>}
        </div>
        <div className="flex gap-2 mt-auto pt-3 border-t border-[#27272A] flex-wrap">
          <Link to={`/somos-ferro/${sectionId}/${p.id}`} className="btn-ghost text-xs flex-1 text-center" data-testid={`read-more-${p.id}`}>
            {isAdmin ? "Ver / Editar →" : "Leer más →"}
          </Link>
          {isAdmin && !inTrash && (
            <>
              {p.status === "published"
                ? <button onClick={() => onAction(() => api.post(`/ferro/posts/${p.id}/unpublish`), "Despublicada")} className="btn-ghost text-xs" data-testid={`unpub-${p.id}`}>Despublicar</button>
                : <button onClick={() => onAction(() => api.post(`/ferro/posts/${p.id}/publish`), "Publicada")} className="btn-primary text-xs" data-testid={`pub-${p.id}`}>Publicar</button>
              }
              <button onClick={() => onAction(() => api.post(`/ferro/posts/${p.id}/pin`, { value: !p.is_pinned }), p.is_pinned ? "Desfijada" : "Fijada")} className="btn-ghost text-xs" title="Fijar arriba" data-testid={`pin-${p.id}`}><Pin size={12}/></button>
              <button onClick={() => onAction(() => api.post(`/ferro/posts/${p.id}/feature`, { value: !p.is_featured }), p.is_featured ? "Destacado off" : "Destacada")} className="btn-ghost text-xs" title="Destacar" data-testid={`feat-${p.id}`}><Star size={12}/></button>
              <button onClick={() => { if (window.confirm(`¿Enviar "${p.title}" a la papelera?`)) onAction(() => api.delete(`/ferro/posts/${p.id}`), "En papelera"); }} className="btn-ghost text-xs text-[#FF3B30] border-[#FF3B30]" title="Eliminar" data-testid={`del-${p.id}`}><Trash2 size={12}/></button>
            </>
          )}
          {isAdmin && inTrash && (
            <button onClick={() => onAction(() => api.post(`/ferro/posts/${p.id}/restore`), "Restaurada")} className="btn-primary text-xs flex items-center gap-1" data-testid={`restore-${p.id}`}><RotateCcw size={12}/> Restaurar</button>
          )}
        </div>
      </div>
    </article>
  );
}

function SectionMetaEditor({ section, onClose, onSaved }) {
  const [d, setD] = useState({
    title: section?.title || "", description: section?.description || "",
    icon: section?.icon || "", visible: section?.visible ?? true, order: section?.order ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/ferro/sections/${section.section_id}`, d);
      onSaved(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tactical-card p-6 w-full max-w-md bg-[#0A0A0A]" onClick={e => e.stopPropagation()} data-testid="section-editor">
        <h3 className="font-display text-xl text-white mb-4">Editar sección</h3>
        <label className="block mb-3">
          <span className="overline block mb-1">Título</span>
          <input className="input-tactical" value={d.title} onChange={e => setD({ ...d, title: e.target.value })} maxLength={120}/>
        </label>
        <label className="block mb-3">
          <span className="overline block mb-1">Descripción</span>
          <input className="input-tactical" value={d.description} onChange={e => setD({ ...d, description: e.target.value })} maxLength={400}/>
        </label>
        <label className="block mb-3">
          <span className="overline block mb-1">Ícono (megaphone, trophy, heart, ...)</span>
          <input className="input-tactical" value={d.icon} onChange={e => setD({ ...d, icon: e.target.value })} maxLength={40}/>
        </label>
        <label className="flex items-center gap-2 mb-4">
          <input type="checkbox" checked={d.visible} onChange={e => setD({ ...d, visible: e.target.checked })}/>
          <span className="text-sm text-white">Visible para usuarios</span>
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="btn-ghost text-xs">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary text-xs" data-testid="section-save">{saving ? "Guardando…" : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}
